import { useMutation, useQuery } from '@tanstack/react-query';

import { resolverBridgeApi } from 'API/resolver-bridge-api/resolver-bridge-api';
import type {
    MappedFileData,
    ParsedDataRow,
    ParsedDataRowValues,
    ResolverJobStatus,
    SupportRequestResultsResponse,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { transformMappedDataForResolver } from 'features/offline-reports/support-requests/components/support-requests-modals/utils/transform-mapped-data-for-resolver/transform-mapped-data-for-resolver';
import {
    resetDataStatuses,
    setDataLoadingStatuses,
    useSetJobResultsQueryData,
} from 'features/offline-reports/support-requests/components/support-requests-modals/utils/utils';
import { resolverBridgeQueryKeys } from 'features/offline-reports/support-requests/components/support-requests-modals/queries/resolver-queries-keys';
import { useSendSupportSplunkEvent } from 'features/offline-reports/support-requests/hooks/use-send-support-splunk-event';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
    mappedFileDataStore,
    resolverJobStore,
} from 'features/offline-reports/support-requests/store/property-store';

const POLLING_INTERVAL = 5000;

export const useCreateResolverJobMutation = () => {
    const setResolverJob = useSetAtom(resolverJobStore);

    return useMutation({
        mutationKey: resolverBridgeQueryKeys.createJob,
        mutationFn: async ({ mappedFileData }: { mappedFileData: MappedFileData }) => {
            const { columns, rows } = transformMappedDataForResolver(
                mappedFileData?.data || [],
                mappedFileData?.matching,
            );

            const resolverJob = await resolverBridgeApi.createJob({
                data: {
                    columns,
                    rows,
                },
                mode: 'first',
            });
            setResolverJob(resolverJob);
            return resolverJob;
        },
    });
};

export const useResolverJobStatusQuery = () => {
    const [resolverJob, setResolverJob] = useAtom(resolverJobStore);

    const { job_id } = resolverJob ?? {};

    return useQuery<ResolverJobStatus>({
        queryKey: resolverBridgeQueryKeys.jobStatus(job_id),
        queryFn: async () => {
            const resolverStatus = await resolverBridgeApi.getJobStatus(job_id as string);

            setResolverJob((prev) => (prev ? { ...prev, status: resolverStatus.status } : prev));

            return resolverStatus;
        },
        refetchInterval: (data) => {
            if (data?.status === 'Ready' || data?.status === 'Error') {
                return false;
            }
            return POLLING_INTERVAL;
        },
        select: (data) => {
            if (data.status === 'Error') {
                throw new Error('Resolver job failed');
            }
            return data;
        },
        enabled: !!job_id,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: true,
    });
};

export const useResolverJobResultsQuery = () => {
    const resolverJob = useAtomValue(resolverJobStore);

    const job_id = resolverJob?.job_id;
    const isReady = resolverJob?.status === 'Ready';

    return useQuery<SupportRequestResultsResponse[]>({
        queryKey: resolverBridgeQueryKeys.jobResults(job_id),
        queryFn: async () => {
            const { data } = await resolverBridgeApi.getJobResults(job_id as string);

            const { columns, rows } = data;

            const jobResults = rows.map((row) => {
                const item: ParsedDataRow = {};
                columns.forEach((col, index) => {
                    item[col] = row[index];
                });
                return item as SupportRequestResultsResponse;
            });

            return jobResults;
        },
        enabled: !!job_id && isReady,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const useUpdateResolverJobMutation = () => {
    const [mappedFileData, setMappedFileData] = useAtom(mappedFileDataStore);
    const setJobResultsQueryData = useSetJobResultsQueryData();
    const sendSplunkEvent = useSendSupportSplunkEvent();

    return useMutation({
        mutationKey: resolverBridgeQueryKeys.mutation,
        mutationFn: async ({
            itemIds,
            shouldRunStatus = true,
            addIsLoading = true,
        }: {
            itemIds: string[];
            shouldRunStatus?: boolean;
            addIsLoading?: boolean;
        }) => {
            const startTime = Date.now();

            addIsLoading && setMappedFileData((prev) => setDataLoadingStatuses(itemIds, prev));

            const itemsToUpdate =
                mappedFileData?.data.filter((item) => itemIds.includes(String(item.id))) || [];
            const { columns, rows } = transformMappedDataForResolver(
                itemsToUpdate,
                mappedFileData?.matching,
            );

            const job = await resolverBridgeApi.createJob({
                data: {
                    columns,
                    rows,
                },
                mode: 'first',
            });

            return {
                job,
                updatedItemIds: itemIds,
                shouldRunStatus,
                startTime,
                addIsLoading,
            };
        },
        onSuccess: async (result) => {
            const pollStatus = async (): Promise<void> => {
                if (!result.shouldRunStatus) {
                    return;
                }

                const status = await resolverBridgeApi.getJobStatus(result.job.job_id);

                if (status.status === 'Error') {
                    return setMappedFileData(resetDataStatuses);
                }

                if (status.status === 'Ready') {
                    const jobResults = await resolverBridgeApi.getJobResults(result.job.job_id);
                    const { columns, rows } = jobResults.data;

                    const newResults = rows.map((row) => {
                        const item: Record<string, ParsedDataRowValues> = {};
                        columns.forEach((col, index) => {
                            item[col] = row[index];
                        });
                        return item as SupportRequestResultsResponse;
                    });

                    const load_time = Date.now() - result.startTime;

                    result.addIsLoading &&
                        sendSplunkEvent({
                            action: 'button-click',
                            value: 'recheck coverage',
                            load_time,
                            report_config: {
                                checked_properties: rows.length,
                                total_properties: mappedFileData?.data.length,
                            },
                        });

                    setMappedFileData(resetDataStatuses);

                    setJobResultsQueryData((oldData) => {
                        if (!oldData) return oldData;

                        return oldData.map((item) => {
                            if (result.updatedItemIds.includes(item.id)) {
                                const newItem = newResults.find(
                                    (newItem) => newItem.id === item.id,
                                );
                                return newItem || item;
                            }
                            return item;
                        });
                    });
                } else {
                    await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));

                    await pollStatus();
                }
            };

            await pollStatus();
        },
    });
};
